import { useAuth0 } from "@auth0/auth0-react";
import { useCallback, useEffect, useState } from "react";

export type UniversalLoginScreenState = "redirecting" | "error";

interface UniversalLoginViewState {
  errorMessage: string;
  screenState: UniversalLoginScreenState;
}

interface UseUniversalLoginRedirectOptions {
  callbackPath: string;
  isConfigured: boolean;
  unavailableMessage?: string;
  retryMessage?: string;
}

interface UseUniversalLoginRedirectResult {
  errorMessage: string;
  isAuthenticated: boolean;
  isLoading: boolean;
  screenState: UniversalLoginScreenState;
  redirectToUniversalLogin: () => Promise<void>;
}

export const useUniversalLoginRedirect = ({
  callbackPath,
  isConfigured,
  unavailableMessage = "Sign in is unavailable.",
  retryMessage = "Please try again.",
}: UseUniversalLoginRedirectOptions): UseUniversalLoginRedirectResult => {
  const { error, isAuthenticated, isLoading, loginWithRedirect } = useAuth0();
  const [viewState, setViewState] = useState<UniversalLoginViewState>({
    errorMessage: "",
    screenState: "redirecting",
  });
  const [hasAttemptedRedirect, setHasAttemptedRedirect] = useState(false);

  const showError = useCallback((message: string) => {
    setViewState({
      errorMessage: message,
      screenState: "error",
    });
  }, []);

  const redirectToUniversalLogin = useCallback(async () => {
    setHasAttemptedRedirect(true);

    if (!isConfigured) {
      showError(unavailableMessage);
      return;
    }

    setViewState({
      errorMessage: "",
      screenState: "redirecting",
    });

    try {
      await loginWithRedirect({
        appState: {
          returnTo: callbackPath,
        },
      });
    } catch {
      showError(retryMessage);
    }
  }, [
    callbackPath,
    isConfigured,
    loginWithRedirect,
    retryMessage,
    showError,
    unavailableMessage,
  ]);

  useEffect(() => {
    if (!error) {
      return;
    }

    showError(retryMessage);
  }, [error, retryMessage, showError]);

  useEffect(() => {
    if (isAuthenticated || hasAttemptedRedirect || error) {
      return;
    }

    if (!isConfigured) {
      redirectToUniversalLogin();
      return;
    }

    if (isLoading) {
      return;
    }

    redirectToUniversalLogin();
  }, [
    error,
    hasAttemptedRedirect,
    isConfigured,
    isAuthenticated,
    isLoading,
    redirectToUniversalLogin,
  ]);

  return {
    errorMessage: viewState.errorMessage,
    isAuthenticated,
    isLoading,
    screenState: viewState.screenState,
    redirectToUniversalLogin,
  };
};
